import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const Agents = () => {
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  const agents = [
    {
      id: 'filing-digester',
      name: 'Filing Digester Agent',
      icon: '📄',
      color: 'bg-blue-100',
      iconColor: 'text-blue-600',
      description: 'Transforms complex SEC filings into clear, actionable summaries',
      longDescription: 'The Filing Digester Agent automatically processes SEC filings (10-K, 10-Q, 8-K, etc.) and extracts the most important information. It identifies key changes, risk factors, and financial highlights, then presents them in a clear, concise format that saves you hours of reading time.',
      features: [
        'Automatic processing of all SEC filing types',
        'Identification of material changes from previous filings',
        'Risk factor analysis and prioritization',
        'Financial statement comparison and trend identification',
        'Management discussion summary with sentiment analysis'
      ],
      useCases: [
        'Quickly understand lengthy quarterly and annual reports',
        'Track changes in risk factors over time',
        'Identify red flags in financial statements',
        'Compare management tone across multiple periods'
      ],
      availableIn: ['Analyst', 'Master Genie'],
      demoImage: '/images/filing-digester-demo.png'
    },
    {
      id: 'peer-comp',
      name: 'Peer Comp Agent',
      icon: '🔍',
      color: 'bg-green-100',
      iconColor: 'text-green-600',
      description: 'Creates comprehensive peer comparisons across multiple metrics',
      longDescription: 'The Peer Comp Agent automatically identifies relevant competitors and builds detailed comparison tables across key financial metrics, valuation multiples, growth rates, and operational KPIs. It helps you understand how a company stacks up against its peers and identify relative strengths and weaknesses.',
      features: [
        'Automatic peer group identification and customization',
        'Comprehensive financial metric comparison',
        'Valuation multiple analysis with historical trends',
        'Growth rate benchmarking across key metrics',
        'Visual charts and heatmaps for easy comparison'
      ],
      useCases: [
        'Identify undervalued or overvalued companies',
        'Benchmark operational performance',
        'Discover sector trends and outliers',
        'Support investment theses with comparative data'
      ],
      availableIn: ['Analyst', 'Master Genie'],
      demoImage: '/images/peer-comp-demo.png'
    },
    {
      id: 'event-monitor',
      name: 'Event Monitor Agent',
      icon: '📢',
      color: 'bg-purple-100',
      iconColor: 'text-purple-600',
      description: 'Tracks news, earnings, and price movements for your watchlist',
      longDescription: 'The Event Monitor Agent continuously scans for significant events related to companies on your watchlist. It tracks earnings announcements, news releases, analyst ratings, insider transactions, and unusual price movements. The agent then delivers timely alerts with summarized information and potential impact analysis.',
      features: [
        'Real-time monitoring of company news and events',
        'Earnings announcement tracking and analysis',
        'Analyst rating change notifications',
        'Insider transaction monitoring',
        'Unusual price movement detection with context'
      ],
      useCases: [
        'Stay informed about breaking news for your portfolio',
        'Get instant earnings summaries and key takeaways',
        'Track institutional sentiment through analyst ratings',
        'Monitor insider activity for potential signals'
      ],
      availableIn: ['Analyst', 'Master Genie'],
      demoImage: '/images/event-monitor-demo.png'
    },
    {
      id: 'model-builder',
      name: 'Model Builder Agent',
      icon: '📊',
      color: 'bg-yellow-100',
      iconColor: 'text-yellow-600',
      description: 'Creates financial models from historical data with future projections',
      longDescription: 'The Model Builder Agent automatically constructs financial models based on historical data and analyst estimates. It creates detailed income statements, balance sheets, and cash flow projections with customizable growth assumptions. The agent can also build DCF models, dividend discount models, and other valuation frameworks.',
      features: [
        'Automated 3-statement financial model creation',
        'Customizable growth and margin assumptions',
        'DCF model construction with sensitivity analysis',
        'Dividend discount modeling for income stocks',
        'Export to Excel with formulas intact'
      ],
      useCases: [
        'Quickly build baseline financial models',
        'Test different growth and margin scenarios',
        'Perform sensitivity analysis on key variables',
        'Generate fair value estimates with multiple methods'
      ],
      availableIn: ['Master Genie'],
      demoImage: '/images/model-builder-demo.png'
    },
    {
      id: 'sector-analyst',
      name: 'Sector Analyst Agent',
      icon: '🏭',
      color: 'bg-red-100',
      iconColor: 'text-red-600',
      description: 'Provides comprehensive sector analysis and trend identification',
      longDescription: 'The Sector Analyst Agent delivers in-depth analysis of entire sectors and industries. It identifies key trends, growth drivers, challenges, and competitive dynamics. The agent also highlights sector leaders and laggards, providing context for individual company analysis within the broader industry landscape.',
      features: [
        'Comprehensive sector overview and structure analysis',
        'Identification of key growth drivers and headwinds',
        'Competitive landscape mapping',
        'Regulatory environment monitoring',
        'Sector-specific KPI tracking and benchmarking'
      ],
      useCases: [
        'Understand sector dynamics before diving into individual stocks',
        'Identify structural changes and disruptions',
        'Discover sector rotation opportunities',
        'Build sector-based investment theses'
      ],
      availableIn: ['Master Genie'],
      demoImage: '/images/sector-analyst-demo.png'
    },
    {
      id: 'custom-agent',
      name: 'Custom Agent Development',
      icon: '🪄',
      color: 'bg-indigo-100',
      iconColor: 'text-indigo-600',
      description: 'Work with our team to build specialized agents for your unique needs',
      longDescription: "Our Custom Agent Development service allows Master Genie subscribers to work directly with our AI and research experts to create specialized agents tailored to specific investment strategies, data sources, or analytical frameworks. We'll build, test, and refine agents that integrate seamlessly with your existing workflow.",
      features: [
        'Consultation with AI and domain experts',
        'Custom agent design and development',
        'Integration with proprietary data sources',
        'Specialized analytical frameworks',
        'Ongoing refinement and optimization'
      ],
      useCases: [
        'Create agents for specialized investment strategies',
        'Build custom screening tools with proprietary criteria',
        'Develop agents that match your analytical process',
        'Automate repetitive research tasks specific to your workflow'
      ],
      availableIn: ['Master Genie'],
      demoImage: '/images/custom-agent-demo.png'
    }
  ];

  const filteredAgents = searchQuery 
    ? agents.filter(agent => 
        agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        agent.description.toLowerCase().includes(searchQuery.toLowerCase())
      )
    : agents;

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <>
      <Navbar />
      <div className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-16">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold mb-4 text-midnight-navy"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              Meet Your Research Agents
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-600 mb-8"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ delay: 0.1 }}
            >
              Our specialized AI agents work tirelessly to deliver insights, analyze data, and transform your research workflow.
            </motion.p>
            
            {/* Search */}
            <motion.div 
              className="relative max-w-md mx-auto"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
              transition={{ delay: 0.2 }}
            >
              <input
                type="text"
                placeholder="Search agents..."
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-pale-gold focus:border-transparent"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <div className="absolute right-3 top-3 text-gray-400">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </motion.div>
          </div>
          
          {/* Agent Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto mb-16">
            {filteredAgents.map((agent, index) => (
              <motion.div 
                key={agent.id}
                className="bg-white rounded-xl overflow-hidden shadow-lg border border-gray-200 hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setSelectedAgent(agent)}
                initial="hidden"
                animate="visible"
                variants={fadeIn}
                transition={{ delay: 0.1 * (index % 3 + 1) }}
              >
                <div className={`${agent.color} p-6`}>
                  <div className={`text-4xl ${agent.iconColor} mb-4`}>{agent.icon}</div>
                  <h3 className="text-xl font-bold text-midnight-navy mb-2">{agent.name}</h3>
                  <p className="text-gray-700 mb-4">{agent.description}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {agent.availableIn.includes('Analyst') && (
                      <span className="bg-blue-500 bg-opacity-10 text-blue-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        Analyst Plan
                      </span>
                    )}
                    {agent.availableIn.includes('Master Genie') && (
                      <span className="bg-purple-500 bg-opacity-10 text-purple-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        Master Genie Plan
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-6 border-t border-gray-100">
                  <h4 className="font-semibold text-midnight-navy mb-4">Key Features:</h4>
                  <ul className="space-y-2 mb-6">
                    {agent.features.slice(0, 3).map((feature, i) => (
                      <li key={i} className="flex items-start">
                        <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700 text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <button className="w-full text-midnight-navy font-medium hover:text-pale-gold transition-colors text-center">
                    Learn More
                  </button>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Agent Detail Modal */}
          {selectedAgent && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <motion.div 
                className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.3 }}
              >
                <div className={`${selectedAgent.color} p-6 relative`}>
                  <button 
                    className="absolute top-4 right-4 text-gray-600 hover:text-gray-900"
                    onClick={() => setSelectedAgent(null)}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  
                  <div className="flex items-center mb-4">
                    <div className={`text-4xl ${selectedAgent.iconColor} mr-4`}>{selectedAgent.icon}</div>
                    <h2 className="text-2xl font-bold text-midnight-navy">{selectedAgent.name}</h2>
                  </div>
                  
                  <p className="text-gray-700 mb-4">{selectedAgent.longDescription}</p>
                  
                  <div className="flex flex-wrap gap-2">
                    {selectedAgent.availableIn.includes('Analyst') && (
                      <span className="bg-blue-500 bg-opacity-10 text-blue-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        Analyst Plan
                      </span>
                    )}
                    {selectedAgent.availableIn.includes('Master Genie') && (
                      <span className="bg-purple-500 bg-opacity-10 text-purple-700 text-xs font-medium px-2.5 py-0.5 rounded-full">
                        Master Genie Plan
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-lg font-semibold text-midnight-navy mb-4">Key Features</h3>
                      <ul className="space-y-3 mb-6">
                        {selectedAgent.features.map((feature, i) => (
                          <li key={i} className="flex items-start">
                            <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      <h3 className="text-lg font-semibold text-midnight-navy mb-4">Common Use Cases</h3>
                      <ul className="space-y-3 mb-6">
                        {selectedAgent.useCases.map((useCase, i) => (
                          <li key={i} className="flex items-start">
                            <svg className="h-5 w-5 text-electric-blue mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            <span className="text-gray-700">{useCase}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div className="bg-gray-100 rounded-lg p-4 flex items-center justify-center">
                      <div className="text-center">
                        <div className={`text-5xl ${selectedAgent.iconColor} mb-4 mx-auto`}>{selectedAgent.icon}</div>
                        <p className="text-gray-500 mb-4">Demo visualization would appear here</p>
                        {!selectedAgent.availableIn.includes('Analyst') && (
                          <div className="bg-purple-100 text-purple-800 text-sm font-medium px-3 py-1 rounded-full inline-flex items-center">
                            <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            Master Genie Exclusive
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8 flex justify-end">
                    <button 
                      className="bg-pale-gold text-midnight-navy px-6 py-2 rounded-lg font-semibold hover:bg-opacity-90 transition-colors"
                      onClick={() => setSelectedAgent(null)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
          
          {/* Feature Wall */}
          <div className="bg-midnight-navy text-white rounded-xl overflow-hidden shadow-lg mb-16">
            <div className="p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold mb-4">Unlock the Full Power of Research Agents</h2>
                  <p className="text-gray-300 mb-6">
                    The Master Genie plan gives you unlimited access to our complete suite of research agents, including exclusive agents not available in other plans.
                  </p>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Access all 6 specialized research agents</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Create custom agents for your specific needs</span>
                    </li>
                    <li className="flex items-start">
                      <svg className="h-5 w-5 text-pale-gold mr-2 mt-0.5 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>Combine agents for powerful workflow automation</span>
                    </li>
                  </ul>
                  <button className="bg-pale-gold text-midnight-navy px-6 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors">
                    Upgrade to Master Genie
                  </button>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                    <div className="text-3xl text-pale-gold mb-2">📊</div>
                    <h3 className="font-semibold mb-1">Model Builder</h3>
                    <p className="text-sm text-gray-300">Master Genie Exclusive</p>
                  </div>
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg">
                    <div className="text-3xl text-pale-gold mb-2">🏭</div>
                    <h3 className="font-semibold mb-1">Sector Analyst</h3>
                    <p className="text-sm text-gray-300">Master Genie Exclusive</p>
                  </div>
                  <div className="bg-white bg-opacity-10 p-4 rounded-lg col-span-2">
                    <div className="text-3xl text-pale-gold mb-2">🪄</div>
                    <h3 className="font-semibold mb-1">Custom Agent Development</h3>
                    <p className="text-sm text-gray-300">Master Genie Exclusive</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Testimonials */}
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-midnight-navy mb-8 text-center">What Our Users Say</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-blue-100 text-blue-600 w-10 h-10 rounded-full flex items-center justify-center font-bold mr-3">JD</div>
                  <div>
                    <h3 className="font-semibold text-midnight-navy">James Donovan</h3>
                    <p className="text-sm text-gray-500">Senior Analyst, Capital Research</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "The Filing Digester Agent has completely transformed how I approach quarterly reports. What used to take me hours now takes minutes, and I don't miss any important details."
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-green-100 text-green-600 w-10 h-10 rounded-full flex items-center justify-center font-bold mr-3">ML</div>
                  <div>
                    <h3 className="font-semibold text-midnight-navy">Maria Lopez</h3>
                    <p className="text-sm text-gray-500">Portfolio Manager, Horizon Investments</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "The Peer Comp Agent gives me insights I couldn't easily get before. It's like having a team of analysts working for me around the clock, identifying opportunities across sectors."
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-purple-100 text-purple-600 w-10 h-10 rounded-full flex items-center justify-center font-bold mr-3">RK</div>
                  <div>
                    <h3 className="font-semibold text-midnight-navy">Robert Kim</h3>
                    <p className="text-sm text-gray-500">Independent Investor</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "As an individual investor, the Event Monitor Agent has been invaluable. I'm always informed about important news for my portfolio companies without having to constantly check multiple sources."
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-sm p-6">
                <div className="flex items-center mb-4">
                  <div className="bg-yellow-100 text-yellow-600 w-10 h-10 rounded-full flex items-center justify-center font-bold mr-3">SC</div>
                  <div>
                    <h3 className="font-semibold text-midnight-navy">Sarah Chen</h3>
                    <p className="text-sm text-gray-500">Research Director, Global Funds</p>
                  </div>
                </div>
                <p className="text-gray-700 italic">
                  "The custom agent development has been a game-changer for our firm. We now have specialized agents that perfectly match our investment process and save us countless hours of manual work."
                </p>
              </div>
            </div>
          </div>
          
          {/* CTA Section */}
          <div className="mt-16 text-center">
            <h2 className="text-2xl font-bold text-midnight-navy mb-4">Ready to transform your research workflow?</h2>
            <p className="text-gray-600 mb-8 max-w-2xl mx-auto">Join thousands of analysts and investors who are already using Research Genie's specialized agents to gain deeper insights and save time.</p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <button className="bg-pale-gold text-midnight-navy px-8 py-3 rounded-lg font-semibold hover:bg-opacity-90 transition-colors">
                Start Free Trial
              </button>
              <button className="border border-midnight-navy text-midnight-navy px-8 py-3 rounded-lg font-semibold hover:bg-midnight-navy hover:text-white transition-colors">
                View Pricing Plans
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default Agents;
